'use client';
/** @jsxImportSource @emotion/react */

import { type ElementType, forwardRef, type Ref } from 'react';

import type {
  OverridableComponent,
  OverrideProps,
} from '@mui/material/OverridableComponent';

import MuiAvatar, {
  type AvatarProps as MuiAvatarProps,
  type AvatarTypeMap as MuiAvatarTypeMap,
} from '@mui/material/Avatar';
import { type SxProps, type Theme, useTheme } from '@mui/material/styles';

type ExcludedMuiProps = '';

type CustomProps = {
  color?: MuiAvatarProps['color'] | 'forceWhite';
  component?: ElementType;
  size?: avatarSizes;
};

type AvatarTypeMap<
  P = CustomProps,
  D extends ElementType = MuiAvatarTypeMap['defaultComponent'],
> = {
  defaultComponent: D;
  props: Omit<MuiAvatarTypeMap['props'], ExcludedMuiProps> & P;
};

/**
 * @deprecated Please use `AvatarProps` from `@zeus/core/next` instead.
 * @see {@link https://zeus.serko.travel/?path=/docs/zeus-core-next-components-avatar-migration--avatar-migration-guide Migration guide}
 */
export type AvatarProps<
  D extends ElementType = MuiAvatarTypeMap['defaultComponent'],
  P = CustomProps,
> = OverrideProps<MuiAvatarTypeMap<P, D>, D>;

/**
 * @deprecated Please use `Avatar` from `@zeus/core/next` instead.
 * @see {@link https://zeus.serko.travel/?path=/docs/zeus-core-next-components-avatar-migration--avatar-migration-guide Migration guide}
 */
export const avatarSize = {
  xs: 16,
  sm: 24,
  md: 32,
  lg: 40,
};

/**
 * @deprecated Please use `Avatar` from `@zeus/core/next` instead.
 * @see {@link https://zeus.serko.travel/?path=/docs/zeus-core-next-components-avatar-migration--avatar-migration-guide Migration guide}
 */
export type avatarSizes = 'lg' | 'md' | 'sm' | 'xs';

const AvatarComponent = (
  { color, size = 'md', sx = [], ...props }: AvatarProps,
  ref: Ref<HTMLDivElement>
) => {
  const theme = useTheme();
  const resolvedSx: SxProps<Theme> = {
    backgroundColor:
      color === 'forceWhite'
        ? theme.palette.forceWhite.main
        : color === undefined
          ? theme.palette.secondary.main
          : color,
    color:
      color === 'forceWhite' ? theme.palette.forceWhite.contrastText : color,
    height: avatarSize[size],
    width: avatarSize[size],
  };

  return (
    <MuiAvatar
      {...props}
      ref={ref}
      sx={[resolvedSx, ...(Array.isArray(sx) ? sx : [sx])]}
    />
  );
};

AvatarComponent.displayName = 'Avatar';

/**
 * @deprecated Please use `Avatar` from `@zeus/core/next` instead.
 * @see {@link https://zeus.serko.travel/?path=/docs/zeus-core-next-components-avatar-migration--avatar-migration-guide Migration guide}
 */
export const Avatar = forwardRef(
  AvatarComponent
) as OverridableComponent<AvatarTypeMap>;
